// src/utils/portfolioHealth.js
//
// Property health scoring for the superuser portfolio dashboard.
//
// Each property gets a 0–100 "risk" score (higher = worse) from a handful of
// operational signals, then a healthScore = 100 - risk that drives both the
// globe point color and the "Properties Needing Attention" ranking.
//
// Weights are intentionally simple and live here (not hard-coded per caller) so
// they can later move to Clients/{clientId}.attentionWeights without touching
// the dashboard.

export const DEFAULT_WEIGHTS = {
  downPct: 40, // multiplied by fraction of inventory down (0–1)
  openWO: 2, // per open work order
  openIssue: 4, // per open property issue
  needsVendor: 3, // per down room that needs a vendor
  stale: 15, // flat penalty when no room activity in > staleHours
  staleHours: 24,
};

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Hours since an ISO timestamp (or Firestore Timestamp-ish). Infinity if unknown. */
export function hoursSince(ts) {
  if (!ts) return Infinity;
  let ms;
  if (typeof ts === "string") ms = Date.parse(ts);
  else if (typeof ts?.toDate === "function") ms = ts.toDate().getTime();
  else if (typeof ts?.seconds === "number") ms = ts.seconds * 1000;
  else return Infinity;
  if (Number.isNaN(ms)) return Infinity;
  return (Date.now() - ms) / 36e5;
}

/**
 * Compute a property's risk (0–100, higher = worse) from its rolled-up metrics.
 * Every input is optional and defaults to a safe zero so partially-populated
 * properties still score.
 */
export function propertyRisk(m, weights = DEFAULT_WEIGHTS) {
  const totalRooms = m.totalRooms || 0;
  const downRooms = m.downRooms || 0;
  const downPct = totalRooms ? downRooms / totalRooms : 0;
  const stale = hoursSince(m.lastUpdate) > weights.staleHours;

  const raw =
    downPct * weights.downPct +
    (m.openWorkOrders || 0) * weights.openWO +
    (m.openIssues || 0) * weights.openIssue +
    (m.needsVendor || 0) * weights.needsVendor +
    (stale ? weights.stale : 0);

  return Math.round(clamp(raw, 0, 100));
}

/** healthScore: 100 (great) … 0 (critical). */
export function propertyHealth(m, weights = DEFAULT_WEIGHTS) {
  return 100 - propertyRisk(m, weights);
}

/** Map a healthScore (0–100) to a status bucket + color for globe points & chips. */
export function healthBucket(healthScore) {
  if (healthScore >= 75) return { label: "Healthy", color: "#2e7d32" }; // green
  if (healthScore >= 50) return { label: "Watch", color: "#f9a825" }; // amber
  if (healthScore >= 25) return { label: "Attention", color: "#ef6c00" }; // orange
  return { label: "Critical", color: "#c62828" }; // red
}

export const healthColor = (healthScore) => healthBucket(healthScore).color;
